import { useEffect, useRef } from 'react';

import { publicEnv } from '@/lib/env';

declare global {
  interface Window {
    grecaptcha: {
      enterprise: {
        ready: (cb: () => void) => void;
        execute: (siteKey: string, options: { action: string }) => Promise<string>;
      };
    };
  }
}

const SITE_KEY = publicEnv.NEXT_PUBLIC_RECAPTCHA_SITE_KEY;
const SCRIPT_ID = 'recaptcha-enterprise-script';

function waitForGrecaptcha(timeoutMs = 10000): Promise<void> {
  return new Promise((resolve, reject) => {
    if (window.grecaptcha?.enterprise) {
      resolve();
      return;
    }

    const start = Date.now();
    const interval = setInterval(() => {
      if (window.grecaptcha?.enterprise) {
        clearInterval(interval);
        resolve();
      } else if (Date.now() - start > timeoutMs) {
        clearInterval(interval);
        reject(new Error('reCAPTCHA Enterprise script load timeout'));
      }
    }, 100);
  });
}

export function useRecaptchaEnterprise() {
  const loaded = useRef(false);

  useEffect(() => {
    if (loaded.current || document.getElementById(SCRIPT_ID)) return;
    loaded.current = true;

    if (!SITE_KEY) {
      console.error('NEXT_PUBLIC_RECAPTCHA_SITE_KEY is not set');
      return;
    }

    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.src = `https://www.google.com/recaptcha/enterprise.js?render=${SITE_KEY}`;
    script.async = true;
    document.head.appendChild(script);
  }, []);

  const executeRecaptcha = async (action: string): Promise<string | null> => {
    try {
      if (!SITE_KEY) {
        console.error('reCAPTCHA: NEXT_PUBLIC_RECAPTCHA_SITE_KEY is not set, skipping');
        return null;
      }

      await waitForGrecaptcha();

      return await new Promise((resolve, reject) => {
        window.grecaptcha.enterprise.ready(async () => {
          try {
            const token = await window.grecaptcha.enterprise.execute(SITE_KEY, { action });
            resolve(token);
          } catch (err) {
            reject(err);
          }
        });
      });
    } catch (err) {
      console.error('reCAPTCHA error:', err);
      return null;
    }
  };

  return { executeRecaptcha };
}
